import { DocumentTypeSchema } from "@src/sudozi-schemas/document-type-schema";
import { RequestStatusSchema } from "@src/sudozi-schemas/request-status-schema";
import { z } from "zod";

export const IntakeCategoryType = z.enum(["INTERNAL", "VENDOR", "PUBLIC"]);

export const addressFieldsSchema = z.object({
  country: z.string(),
  address1: z.string(),
  address2: z.string(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
  memo: z.string().optional(),
});

export enum IntakeQuestionType {
  ADDRESS = "ADDRESS",
  DOCUMENTS = "DOCUMENTS",
  DISPLAY_ONLY = "DISPLAY_ONLY",
  TEXT = "TEXT",
  EMAIL = "EMAIL",
  DATE = "DATE",
  DURATION = "DURATION",
  NUMBER = "NUMBER",
  MONEY = "MONEY",
  YES_NO = "YES_NO",
  REQUEST_AMOUNT = "REQUEST_AMOUNT",
  START_DATE = "START_DATE",
  END_DATE = "END_DATE",
  DUE_DATE = "DUE_DATE",
  NEEDS_REQUEST_SIGNER = "NEEDS_REQUEST_SIGNER",
  CUSTOM_SELECT = "CUSTOM_SELECT",
  VENDOR_SELECT = "VENDOR_SELECT",
  SERVICE_SELECT = "SERVICE_SELECT",
  BUSINESS_UNIT_SELECT = "BUSINESS_UNIT_SELECT",
  SPEND_CATEGORY_SELECT = "SPEND_CATEGORY_SELECT",
  MEMBER_SELECT = "MEMBER_SELECT",
  DEPARTMENT_SELECT = "DEPARTMENT_SELECT",
  LOCATION_SELECT = "LOCATION_SELECT",
  REQUEST_OWNER_SELECT = "REQUEST_OWNER_SELECT",
  REQUEST_TITLE = "REQUEST_TITLE",
  ENTITY_SELECT = "ENTITY_SELECT",
}

type EnumStringValue = `${IntakeQuestionType}`;
function toValue(enumVal: IntakeQuestionType): EnumStringValue {
  return enumVal;
}

export const IntakeQuestionTypeSchema = z.enum([
  toValue(IntakeQuestionType.ADDRESS),
  toValue(IntakeQuestionType.DOCUMENTS),
  toValue(IntakeQuestionType.DISPLAY_ONLY),
  toValue(IntakeQuestionType.TEXT),
  toValue(IntakeQuestionType.EMAIL),
  toValue(IntakeQuestionType.DATE),
  toValue(IntakeQuestionType.DURATION),
  toValue(IntakeQuestionType.NUMBER),
  toValue(IntakeQuestionType.MONEY),
  toValue(IntakeQuestionType.YES_NO),
  toValue(IntakeQuestionType.REQUEST_AMOUNT),
  toValue(IntakeQuestionType.START_DATE),
  toValue(IntakeQuestionType.END_DATE),
  toValue(IntakeQuestionType.DUE_DATE),
  toValue(IntakeQuestionType.NEEDS_REQUEST_SIGNER),
  toValue(IntakeQuestionType.CUSTOM_SELECT),
  toValue(IntakeQuestionType.VENDOR_SELECT),
  toValue(IntakeQuestionType.SERVICE_SELECT),
  toValue(IntakeQuestionType.BUSINESS_UNIT_SELECT),
  toValue(IntakeQuestionType.SPEND_CATEGORY_SELECT),
  toValue(IntakeQuestionType.MEMBER_SELECT),
  toValue(IntakeQuestionType.DEPARTMENT_SELECT),
  toValue(IntakeQuestionType.LOCATION_SELECT),
  toValue(IntakeQuestionType.REQUEST_OWNER_SELECT),
  toValue(IntakeQuestionType.REQUEST_TITLE),
  toValue(IntakeQuestionType.ENTITY_SELECT),
]);

export type IntakeQuestionTypeUnion = z.infer<typeof IntakeQuestionTypeSchema>;

export const CreateIntakeSubmissionFieldSchema = z.union([
  z.object({
    stepId: z.string(),
    questionId: z.string(),
    questionOrdinal: z.number(),
    label: z.string(),
    description: z.string().nullable(),
    isRequired: z.boolean(),
    type: z.union([
      z.literal(IntakeQuestionType.BUSINESS_UNIT_SELECT),
      z.literal(IntakeQuestionType.CUSTOM_SELECT),
      z.literal(IntakeQuestionType.DEPARTMENT_SELECT),
      z.literal(IntakeQuestionType.LOCATION_SELECT),
      z.literal(IntakeQuestionType.MEMBER_SELECT),
      z.literal(IntakeQuestionType.SPEND_CATEGORY_SELECT),
      z.literal(IntakeQuestionType.VENDOR_SELECT),
      z.literal(IntakeQuestionType.SERVICE_SELECT),
      z.literal(IntakeQuestionType.ENTITY_SELECT),
    ]),
    isMulti: z.literal(true),
    value: z.array(z.string()),
    note: z.string().nullish(),
  }),
  z.object({
    stepId: z.string(),
    questionId: z.string(),
    questionOrdinal: z.number(),
    label: z.string(),
    description: z.string().nullable(),
    isRequired: z.boolean(),
    type: z.literal("DEPARTMENT_SELECT_V2"),
    isMulti: z.literal(true),
    value: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        departmentApproverAssigneeUserId: z.string().nullable(),
      }),
    ),
    requireDepartmentApproverAssigneeSelection: z.boolean(),
    note: z.string().nullish(),
  }),
  z.object({
    stepId: z.string(),
    questionId: z.string(),
    questionOrdinal: z.number(),
    label: z.string(),
    description: z.string().nullable(),
    isRequired: z.boolean(),
    type: z.union([
      z.literal(IntakeQuestionType.BUSINESS_UNIT_SELECT),
      z.literal(IntakeQuestionType.CUSTOM_SELECT),
      z.literal(IntakeQuestionType.DEPARTMENT_SELECT),
      z.literal(IntakeQuestionType.LOCATION_SELECT),
      z.literal(IntakeQuestionType.MEMBER_SELECT),
      z.literal(IntakeQuestionType.SPEND_CATEGORY_SELECT),
      z.literal(IntakeQuestionType.VENDOR_SELECT),
      z.literal(IntakeQuestionType.SERVICE_SELECT),
      z.literal(IntakeQuestionType.REQUEST_OWNER_SELECT),
      z.literal(IntakeQuestionType.ENTITY_SELECT),
    ]),
    isMulti: z.literal(false),
    value: z.string().nullable(),
    note: z.string().nullish(),
    version: z.number().default(1),
  }),
  z.object({
    stepId: z.string(),
    questionId: z.string(),
    questionOrdinal: z.number(),
    label: z.string(),
    description: z.string().nullable(),
    isRequired: z.boolean(),
    type: z.literal("DEPARTMENT_SELECT_V2"),
    isMulti: z.literal(false),
    value: z
      .object({
        id: z.string(),
        name: z.string(),
        departmentApproverAssigneeUserId: z.string().nullable(),
      })
      .nullable(),
    requireDepartmentApproverAssigneeSelection: z.boolean(),
    note: z.string().nullish(),
  }),
  z.object({
    stepId: z.string(),
    questionId: z.string(),
    questionOrdinal: z.number(),
    label: z.string(),
    description: z.string().nullable(),
    isRequired: z.boolean(),
    type: z.union([
      z.literal(IntakeQuestionType.DATE),
      z.literal(IntakeQuestionType.START_DATE),
      z.literal(IntakeQuestionType.END_DATE),
      z.literal(IntakeQuestionType.DUE_DATE),
    ]),
    value: z.preprocess(
      (arg) => (typeof arg === "string" && arg.trim() === "" ? null : arg),
      z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    ),
    note: z.string().nullish(),
    version: z.number().default(1),
  }),
  z.object({
    stepId: z.string(),
    questionId: z.string(),
    questionOrdinal: z.number(),
    label: z.string(),
    description: z.string().nullable(),
    isRequired: z.boolean(),
    type: z.literal(IntakeQuestionType.DISPLAY_ONLY),
    value: z.null(),
    note: z.string().nullish(),
    version: z.number().default(1),
  }),
  z.object({
    stepId: z.string(),
    questionId: z.string(),
    questionOrdinal: z.number(),
    label: z.string(),
    description: z.string().nullable(),
    isRequired: z.boolean(),
    type: z.literal(IntakeQuestionType.NUMBER),
    value: z.number().nullable(),
    note: z.string().nullish(),
    version: z.number().default(1),
  }),
  z.object({
    stepId: z.string(),
    questionId: z.string(),
    questionOrdinal: z.number(),
    label: z.string(),
    description: z.string().nullable(),
    isRequired: z.boolean(),
    type: z.literal(IntakeQuestionType.DURATION),
    value: z
      .object({
        years: z.number().nullable(),
        months: z.number().nullable(),
        days: z.number().nullable(),
      })
      .nullable(),
    note: z.string().nullish(),
    version: z.number().default(1),
  }),
  z.object({
    stepId: z.string(),
    questionId: z.string(),
    questionOrdinal: z.number(),
    label: z.string(),
    description: z.string().nullable(),
    isRequired: z.boolean(),
    type: z.union([
      z.literal(IntakeQuestionType.TEXT),
      z.literal(IntakeQuestionType.REQUEST_TITLE),
    ]),
    value: z.string().nullable(),
    note: z.string().nullish(),
    version: z.number().default(1),
  }),
  z.object({
    stepId: z.string(),
    questionId: z.string(),
    questionOrdinal: z.number(),
    label: z.string(),
    description: z.string().nullable(),
    isRequired: z.boolean(),
    type: z.literal(IntakeQuestionType.EMAIL),
    value: z.string().nullable(),
    note: z.string().nullish(),
    version: z.number().default(1),
  }),
  z.object({
    stepId: z.string(),
    questionId: z.string(),
    questionOrdinal: z.number(),
    label: z.string(),
    description: z.string().nullable(),
    isRequired: z.boolean(),
    type: z.literal(IntakeQuestionType.YES_NO),
    value: z.boolean().nullable(),
    note: z.string().nullish(),
    version: z.number().default(1),
  }),
  z.object({
    stepId: z.string(),
    questionId: z.string(),
    questionOrdinal: z.number(),
    label: z.string(),
    description: z.string().nullable(),
    isRequired: z.boolean(),
    type: z.union([
      z.literal(IntakeQuestionType.MONEY),
      z.literal(IntakeQuestionType.REQUEST_AMOUNT),
    ]),
    value: z
      .object({ value: z.string().or(z.number()), currency: z.string() })
      .nullable(),
    note: z.string().nullish(),
    version: z.number().default(1),
  }),
  z.object({
    stepId: z.string(),
    questionId: z.string(),
    questionOrdinal: z.number(),
    label: z.string(),
    description: z.string().nullable(),
    isRequired: z.boolean(),
    type: z.literal(IntakeQuestionType.DOCUMENTS),
    documentTypes: DocumentTypeSchema.array().optional(),
    value: z.array(z.string()),
    note: z.string().nullish(),
    version: z.number().default(1),
  }),
  z.object({
    stepId: z.string(),
    questionId: z.string(),
    questionOrdinal: z.number(),
    label: z.string(),
    description: z.string().nullable(),
    isRequired: z.boolean(),
    type: z.literal(IntakeQuestionType.ADDRESS),
    value: addressFieldsSchema,
    note: z.string().nullish(),
    version: z.number().default(1),
  }),
  z.object({
    stepId: z.string(),
    questionId: z.string(),
    questionOrdinal: z.number(),
    label: z.string(),
    description: z.string().nullable(),
    isRequired: z.boolean(),
    type: z.literal(IntakeQuestionType.NEEDS_REQUEST_SIGNER),
    value: z.boolean(),
    note: z.string().nullish(),
    version: z.number().default(1),
  }),
]);

export type CreateIntakeSubmissionFieldSchema = z.infer<
  typeof CreateIntakeSubmissionFieldSchema
>;

export const IntakeFormStepSchema = z.object({
  stepId: z.string(),
  stepLabel: z.string(),
  stepOrdinal: z.number(),
  fields: CreateIntakeSubmissionFieldSchema.array(),
});

export const CreateIntakeSubmissionSchema = z.object({
  id: z.string().nullable(),
  isDraft: z.boolean(),
  intakeCategoryId: z.string(),
  watcherUserIds: z.array(z.string()),
  skipApprovals: z.boolean().optional(),
  steps: z.array(IntakeFormStepSchema),
});

export type CreateIntakeSubmissionSchema = z.infer<
  typeof CreateIntakeSubmissionSchema
>;

export type VendorIntakeFormStepSchema = z.infer<typeof IntakeFormStepSchema>;

export const IntakeSubmissionNeedsAttentionSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  createdAt: z.string().datetime(),
  approvedOrRejectedAt: z.string().datetime().nullish(),
  isDraft: z.boolean(),
  status: RequestStatusSchema,
  createdByUserId: z.string(),
  number: z.number(),
  intakeCategoryId: z.string(),
  assigneeUserId: z.string().nullable(),
  forVendor: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullish(),
  vendors: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .array(),
  asDelegateFor: z
    .object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .nullable(),
  intakeCategory: z.object({
    id: z.string(),
    name: z.string(),
    type: IntakeCategoryType,
  }),
  createdBy: z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
  }),
  levelGroupStatuses: z.array(
    z.object({
      id: z.string(),
      level: z.number(),
      status: RequestStatusSchema,
      label: z.string(),
      task: z
        .object({
          id: z.string(),
          companyId: z.string(),
          levelGroupStatusId: z.string(),
          description: z.string(),
        })
        .nullish(),
      sendVendorFormTask: z
        .object({
          vendorIntakeCategoryFormsToSend: z.array(
            z.object({
              id: z.string(),
              name: z.string(),
            }),
          ),
        })
        .nullable(),
      netsuitePurchaseOrder: z
        .object({
          id: z.string(),
        })
        .nullable(),
      additionalStepTask: z
        .object({
          id: z.string(),
        })
        .nullable(),
    }),
  ),
});

export type IntakeSubmissionNeedsAttentionSchema = z.infer<
  typeof IntakeSubmissionNeedsAttentionSchema
>;
